import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '../../../components/ui/Modal';
import { Button, Alert } from '../../../components/shared/UIComponents';
import { CheckCircleIcon, XCircleIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { useAccessRequestListener } from '../hooks/useAccessRequestListener';
import { ROUTES } from '../../../utils/constants';

/**
 * SchoolInvitationModal - Shows when admin approves a teacher's access request
 *
 * Displays:
 * - School name
 * - Assigned role (Teacher/Admin/Receptionist)
 * - Accept/Decline buttons
 *
 * On Accept:
 * - Updates user document with schoolId and role
 * - Redirects to dashboard
 *
 * On Decline:
 * - Deletes access request
 * - Stays on current page
 */
const SchoolInvitationModal = () => {
  const { pendingInvitation, acceptInvitation, declineInvitation, loading: hookLoading } = useAccessRequestListener();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleAccept = async () => {
    try {
      setProcessing(true);
      setError(null);

      await acceptInvitation();

      // Show success message and redirect
      // Note: The SchoolContext will auto-update due to user doc change
      navigate(ROUTES.TEACHER_DASHBOARD, {
        state: {
          message: `Welcome to ${pendingInvitation.schoolName}!`
        }
      });
    } catch (err) {
      console.error('[SchoolInvitationModal] Error accepting invitation:', err);
      setError(err.message || 'Failed to accept invitation. Please try again.');
      setProcessing(false);
    }
  };

  const handleDecline = async () => {
    try {
      setProcessing(true);
      setError(null);

      await declineInvitation();

      // Modal will auto-close since pendingInvitation becomes null
      setProcessing(false);
    } catch (err) {
      console.error('[SchoolInvitationModal] Error declining invitation:', err);
      setError(err.message || 'Failed to decline invitation. Please try again.');
      setProcessing(false);
    }
  };

  // Don't show modal if no pending invitation or still loading
  if (hookLoading || !pendingInvitation) {
    return null;
  }

  // Format role name for display
  const roleDisplay = pendingInvitation.assignedRole.charAt(0).toUpperCase() +
                     pendingInvitation.assignedRole.slice(1);

  return (
    <Modal
      isOpen={!!pendingInvitation}
      onClose={() => {}} // Prevent closing by clicking outside - user must choose
      title="School Invitation"
      size="md"
    >
      <div className="space-y-6">
        {/* School Info */}
        <div className="flex items-center gap-4 p-4 bg-base-200 rounded-lg">
          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
            <BuildingOfficeIcon className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-lg">{pendingInvitation.schoolName}</h3>
            <p className="text-sm text-base-content/70">wants to add you as {roleDisplay}</p>
          </div>
        </div>

        {/* Message */}
        <div className="text-center">
          <p className="text-base-content/80">
            An administrator at <strong>{pendingInvitation.schoolName}</strong> has
            invited you to join as <strong>{roleDisplay}</strong>.
          </p>
          <p className="text-sm text-base-content/60 mt-2">
            Do you want to accept this invitation?
          </p>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert type="error">{error}</Alert>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button
            variant="ghost"
            className="flex-1 gap-2"
            onClick={handleDecline}
            disabled={processing}
          >
            <XCircleIcon className="w-5 h-5" />
            Decline
          </Button>
          <Button
            variant="primary"
            className="flex-1 gap-2"
            onClick={handleAccept}
            loading={processing}
            disabled={processing}
          >
            <CheckCircleIcon className="w-5 h-5" />
            Accept
          </Button>
        </div>

        {/* Additional Info */}
        <div className="text-xs text-base-content/50 text-center">
          You can change your school association later in account settings
        </div>
      </div>
    </Modal>
  );
};

export default SchoolInvitationModal;
